import { z } from 'zod'
import { InsiRechercheResultSchema } from '@/lib/Ameli/INSi/insiTypes'

/**
 * Schemas Zod de mutation pour la deduplication / creation par recherche INSi
 * (F6 lookup-by-vitale, F7 lookup-by-ins/traits + from-ins). Regle 7 du CLAUDE.md :
 * toute mutation REST doit etre validee par un schema Zod.
 *
 * Les endpoints API correspondants existent deja (phase A4) :
 *  - POST /care/dossier/lookup-by-vitale  (CareDossierLookupByVitaleInput)
 *  - POST /care/dossier/lookup-by-ins     (CareDossierLookupByInsInput)
 *  - POST /care/dossier/lookup-by-traits  (CareDossierLookupByTraitsInput)
 *  - POST /care/dossier/from-ins          (corps signe { result, signature, expiresAt, nonce })
 */

/**
 * Payload lookup-by-vitale : liste des NIR lus en carte. L'API valide chaque NIR
 * (13-15 car., matricules corses inclus) ; on reste permissif cote front (chaine
 * non vide) et on laisse l'API rejeter les formats invalides.
 */
export const lookupByVitaleBodySchema = z.object({
  numeroSecus: z.array(z.string().min(1)).min(1).max(10),
})
export type LookupByVitaleBody = z.infer<typeof lookupByVitaleBodySchema>

/** Payload lookup-by-ins : matricule INS + OID de l'autorite d'affectation. */
export const lookupByInsBodySchema = z.object({
  matricule: z.string().min(1),
  oid: z.string().min(1),
})
export type LookupByInsBody = z.infer<typeof lookupByInsBodySchema>

/** Payload lookup-by-traits : pivot d'identite (nom + prenom + date ISO). */
export const lookupByTraitsBodySchema = z.object({
  lastname: z.string().min(1),
  firstname: z.string().min(1),
  birthdate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Format attendu : yyyy-mm-dd'),
})
export type LookupByTraitsBody = z.infer<typeof lookupByTraitsBodySchema>

/**
 * Payload from-ins : creation EXPLICITE d'un dossier a partir d'un resultat INSi
 * (WS_INS2). Contrat identique au PATCH /ins mais SANS `channel` (force a `traits`
 * cote serveur) et SANS `id` (creation). `result`, `signature` et `expiresAt` sont
 * repropages TELS QUELS renvoyes par l'execute (integrite HMAC, findings A2/A3).
 */
export const fromInsBodySchema = z.object({
  /** Payload metier InsiResult::toArray (forme a plat, signe par HMAC). */
  result: InsiRechercheResultSchema,
  /** HMAC du payload result, renvoye par l'execute (repropage tel quel). */
  signature: z.string().min(1),
  /** Timestamp Unix d'expiration de la signature (TTL), repropage tel quel. */
  expiresAt: z.number().int().positive(),
  /**
   * Nonce a usage unique renvoye par l'execute, repropage tel quel.
   *
   * La signature de ce parcours est liee a la cible « creation » (dossierId 0), au groupement, a
   * l'utilisateur et a ce nonce (point R004). Elle ne vaut donc ni pour un dossier existant, ni
   * une seconde fois.
   */
  nonce: z.string().min(1),
})
export type FromInsBody = z.infer<typeof fromInsBodySchema>
